import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { Row, Col, Button } from 'reactstrap';
import moment from 'moment';
import Sidebar from '../../Sidebar';
import { useNavigate } from 'react-router';
import { useCookies } from 'react-cookie';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import ErrorToast from '../../ErrorToast';
import { GradientCard } from '../../components/ListPageGradientSummary';
import { displayError } from '../../helpers';
import '../../button.css';
import { getCurrentDate, getCurrentTime } from '../../utils/DateUtils';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllProducts } from '../../services/ProductServices';
import { getAllEquipments } from '../../services/EquipmentServices';
import TableRowsDieselConsumption from './TableRowsDieselConsumption';
import { createDieselConsumption, getDieselConsumptionNumber, getDieselDriverOptions } from '../../services/DieselConsumptionServices';
import { getStockStatement } from '../../services/ConsumptionNoteServices';
import { loadStoredDrivers, persistDriverName, toDriverOptions } from './dieselDriverUtils';
import { buildDieselStockSummaryCards } from './dieselStockUtils';

function emptyLine(lineNo = 1) {
    return {
        line_no: lineNo,
        equipment_id: '',
        equipment_name: '',
        driver_name: '',
        purpose: '',
        product_id: '',
        product_name: '',
        opening_meter: 0,
        closing_meter: 0,
        consumed_qty: 0,
        unit: 'Ltr',
    };
}

function DieselConsumption() {
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [plants, setPlants] = useState([]);
    const [equipments, setEquipments] = useState([]);
    const [products, setProducts] = useState([]);
    const [driverOptions, setDriverOptions] = useState([]);
    const [stockByProduct, setStockByProduct] = useState({});
    const [rowsData, setRowsData] = useState([emptyLine(1)]);
    const [inProgress, setInProgress] = useState(false);
    const [inputs, setInputs] = useState({
        plant_id: '',
        consumption_date: getCurrentDate(),
        consumption_time: getCurrentTime(),
        consumption_no: '',
        prefix: '',
        issued_by: '',
        remarks: '',
    });

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || []).filter((p) => p.status).map((p) => ({
                value: p.id,
                label: p.plant_alias,
            })));
        });
        getAllEquipments(cookies).then((response) => {
            const list = response.equipment_list || response.equipment_data || response.equipments || [];
            setEquipments(list.filter((e) => e.status === true || e.status === 1 || e.status === 'Active'));
        });
        getAllProducts(cookies).then((response) => {
            const list = response.product_list || response.product_data || response.products || [];
            setProducts(list.filter((p) => p.status));
        });
        getDieselDriverOptions(cookies)
            .then((res) => {
                setDriverOptions(toDriverOptions([...(res.drivers || []), ...loadStoredDrivers()]));
            })
            .catch(() => {
                setDriverOptions(toDriverOptions(loadStoredDrivers()));
            });
    }, [cookies]);

    const dieselProducts = useMemo(
        () => products.filter((p) => {
            const name = String(p.name || '').toLowerCase();
            const unit = String(p.unit?.name || p.unit_name || '').toLowerCase();
            return name.includes('diesel') || unit.includes('liter') || unit.includes('litre') || unit.includes('kiloliter');
        }),
        [products],
    );

    const loadNumber = (plantId, date) => {
        if (!plantId || !date) return;
        const displayDate = moment(date, 'YYYY-MM-DD').format('DD-MM-YYYY');
        getDieselConsumptionNumber(cookies, plantId, displayDate)
            .then((res) => {
                setInputs((prev) => ({
                    ...prev,
                    consumption_no: res.consumption_no,
                    prefix: res.prefix,
                }));
            })
            .catch((error) => displayError(error?.response?.data || error, 'Number'));
    };

    useEffect(() => {
        if (inputs.plant_id && inputs.consumption_date) {
            loadNumber(inputs.plant_id, inputs.consumption_date);
        }
    }, [inputs.plant_id, inputs.consumption_date]);

    useEffect(() => {
        if (!inputs.plant_id || !inputs.consumption_date) {
            setStockByProduct({});
            return;
        }
        const asOnDate = moment(inputs.consumption_date, 'YYYY-MM-DD').format('DD-MM-YYYY');
        getStockStatement(cookies, Number(inputs.plant_id), asOnDate, 'cumulative')
            .then((res) => {
                const map = {};
                (res.stock_statement || []).forEach((row) => {
                    if (row.product_id != null) {
                        map[String(row.product_id)] = row;
                    }
                });
                setStockByProduct(map);
            })
            .catch(() => setStockByProduct({}));
    }, [cookies, inputs.plant_id, inputs.consumption_date]);

    useEffect(() => {
        if (dieselProducts.length === 1 && rowsData.length === 1 && !rowsData[0].product_id) {
            const product = dieselProducts[0];
            setRowsData([{
                ...rowsData[0],
                product_id: product.id,
                product_name: product.name,
                unit: product.unit?.name || product.unit_name || 'Ltr',
            }]);
        }
    }, [dieselProducts]);

    const totalQty = useMemo(
        () => rowsData.reduce((sum, row) => sum + (Number(row.consumed_qty) || 0), 0),
        [rowsData],
    );

    const summaryCards = useMemo(
        () => buildDieselStockSummaryCards(stockByProduct, {
            dieselProductIds: dieselProducts.map((p) => p.id),
        }),
        [stockByProduct, dieselProducts],
    );

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs((prev) => ({ ...prev, [name]: value }));
    };

    const handleAddLine = () => {
        setRowsData((prev) => [...prev, emptyLine(prev.length + 1)]);
    };

    const handleCreateDriver = (name) => {
        persistDriverName(name);
        setDriverOptions((prev) => toDriverOptions([...(prev.map((o) => o.value)), name]));
    };

    const handleRemoveLine = (index) => {
        setRowsData((prev) => prev.filter((_, i) => i !== index).map((row, i) => ({
            ...row,
            line_no: i + 1,
        })));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!inputs.plant_id) {
            Swal.fire('Validation', 'Please select plant.', 'warning');
            return;
        }
        if (!rowsData.length || rowsData.every((row) => !(Number(row.consumed_qty) > 0))) {
            Swal.fire('Validation', 'Enter consumed quantity for at least one line.', 'warning');
            return;
        }

        const payload = {
            plant_id: Number(inputs.plant_id),
            consumption_date: moment(inputs.consumption_date, 'YYYY-MM-DD').format('DD-MM-YYYY'),
            consumption_time: inputs.consumption_time,
            consumption_no: inputs.consumption_no,
            prefix: inputs.prefix,
            issued_by: inputs.issued_by,
            remarks: inputs.remarks,
            detail_list: rowsData.map((row, index) => ({
                line_no: index + 1,
                equipment_id: row.equipment_id || null,
                driver_name: row.driver_name,
                purpose: row.purpose,
                product_id: row.product_id,
                opening_meter: Number(row.opening_meter) || 0,
                closing_meter: Number(row.closing_meter) || 0,
                consumed_qty: Number(row.consumed_qty) || 0,
                unit: row.unit || 'Ltr',
            })),
        };

        setInProgress(true);
        try {
            const res = await createDieselConsumption(cookies, payload);
            Swal.fire('Saved', 'Diesel consumption note saved successfully.', 'success');
            navigate(`/DieselConsumptionEdit/${res.id}`);
        } catch (error) {
            displayError(error?.response?.data || error, 'Save Failed');
        } finally {
            setInProgress(false);
        }
    };

    const handleCancel = () => {
        setInputs({
            plant_id: '',
            consumption_date: getCurrentDate(),
            consumption_time: getCurrentTime(),
            consumption_no: '',
            prefix: '',
            issued_by: '',
            remarks: '',
        });
        setRowsData([emptyLine(1)]);
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <ErrorToast />
            <LoadingOverlay active={inProgress} />
            <div id="page-wrap">
                <form onSubmit={handleSubmit}>
                    <FloatingControls
                        tableLink="/DieselConsumptionTable"
                        enableCancel
                        onCancel={handleCancel}
                    />

                    <FormPageLayout
                        moduleLabel="STORE"
                        title="Diesel Consumption"
                        subtitle="Select plant, enter equipment and meter readings, then save the diesel consumption voucher."
                        modeLabel="CREATE MODE"
                        headerActions={summaryCards.map((card) => (
                            <GradientCard key={card.label} card={card} />
                        ))}
                    >
                    <div className="container form-control-panel">
                        <Row className="form-equal-height-row">
                            <Col xl={7} lg={12} md={12}>
                                <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel form-panel-fill">
                                    <h4 className="col-sm-11"><b>Voucher Details</b></h4><br /><br />

                                    <label htmlFor="plant_id" className="form-group col-sm-4 text-right">Plant</label>
                                    <select
                                        id="plant_id"
                                        name="plant_id"
                                        className="form-control col-sm-7 browser-default custom-select mandatory-form-control"
                                        value={inputs.plant_id}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Select Plant</option>
                                        {plants.map((p) => (
                                            <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                    </select><br />

                                    <label htmlFor="issued_by" className="form-group col-sm-4 text-right">Issued By</label>
                                    <input
                                        id="issued_by"
                                        name="issued_by"
                                        className="form-control col-sm-7"
                                        value={inputs.issued_by}
                                        onChange={handleChange}
                                    /><br />

                                    <label htmlFor="remarks" className="form-group col-sm-4 text-right">Remarks</label>
                                    <textarea
                                        id="remarks"
                                        name="remarks"
                                        className="form-control col-sm-7"
                                        rows="2"
                                        value={inputs.remarks}
                                        onChange={handleChange}
                                    /><br />
                                </div>
                            </Col>

                            <Col xl={5} lg={12} md={12}>
                                <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel form-panel-fill">
                                    <h4 className="col-sm-11"><b>Document Details</b></h4><br /><br />

                                    <label className="form-group col-sm-4 text-right">Consumption No</label>
                                    <input
                                        className="form-control col-sm-7"
                                        value={`${inputs.prefix || ''}/${inputs.consumption_no || ''}`}
                                        readOnly
                                    /><br />

                                    <label htmlFor="consumption_date" className="form-group col-sm-4 text-right">Date</label>
                                    <input
                                        type="date"
                                        id="consumption_date"
                                        name="consumption_date"
                                        className="form-control col-sm-7 mandatory-form-control"
                                        value={inputs.consumption_date}
                                        onChange={handleChange}
                                        required
                                    /><br />

                                    <label htmlFor="consumption_time" className="form-group col-sm-4 text-right">Time</label>
                                    <input
                                        type="time"
                                        id="consumption_time"
                                        name="consumption_time"
                                        className="form-control col-sm-7"
                                        value={inputs.consumption_time}
                                        onChange={handleChange}
                                    /><br />
                                </div>
                            </Col>
                        </Row>

                        <div className="table-responsive shadow p-2 my-2 border-secondary">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h4 className="mb-0"><b>Diesel Consumption</b></h4>
                                <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                                    <label className="mb-0 font-weight-bold">Total Diesel Qty</label>
                                    <input
                                        className="form-control form-control-sm"
                                        value={totalQty.toFixed(3)}
                                        readOnly
                                        style={{ width: '110px', textAlign: 'right', fontWeight: 'bold' }}
                                    />
                                </div>
                            </div>
                            <table className="table table-bordered table-sm">
                                <thead className="thead-light text-center">
                                    <tr>
                                        <th style={{ width: 70 }}>Action</th>
                                        <th>#</th>
                                        <th>Purpose</th>
                                        <th>Equipment</th>
                                        <th>Driver</th>
                                        <th>Diesel Product</th>
                                        <th>Opening Meter</th>
                                        <th>Closing Meter</th>
                                        <th>Consumed Qty</th>
                                        <th>Unit</th>
                                        <th>K(HM) Reading</th>
                                        <th>Avg K(H)M/ Ltr.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <TableRowsDieselConsumption
                                        rowsData={rowsData}
                                        equipments={equipments}
                                        dieselProducts={dieselProducts}
                                        driverOptions={driverOptions}
                                        stockByProduct={stockByProduct}
                                        plantSelected={Boolean(inputs.plant_id)}
                                        onChange={setRowsData}
                                        onAdd={handleAddLine}
                                        onRemove={handleRemoveLine}
                                        onCreateDriver={handleCreateDriver}
                                    />
                                </tbody>
                            </table>
                        </div>

                        <div className="text-center my-3">
                            <Button type="submit" className="btn btn-twitter" style={{ width: '100px', fontWeight: 'bold' }}>
                                Save
                            </Button>
                            <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={handleCancel}>
                                Cancel
                            </Button>
                            <Button type="button" className="btn btn-twitter" style={{ width: '110px', fontWeight: 'bold' }} onClick={() => navigate('/DieselConsumptionTable')}>
                                View
                            </Button>
                            <Button type="button" className="btn btn-twitter" style={{ width: '110px', fontWeight: 'bold' }} onClick={() => navigate('/Home')}>
                                Home
                            </Button>
                        </div>
                    </div>
                    </FormPageLayout>
                </form>
            </div>
        </div>
    );
}

export default DieselConsumption;
